<template>
  <a-select
    :value="value"
    :placeholder="placeholder || $t('comptabilite.accountingClassPlaceholder')"
    :disabled="disabled"
    :allow-clear="allowClear"
    show-search
    option-filter-prop="children"
    :filter-option="filterOption"
    :loading="loading"
    @change="onChange"
  >
    <a-select-opt-group v-for="group in groups" :key="group.code">
      <span slot="label" class="plan-comptable-group-label">
        {{ group.code }} – {{ group.label }}
      </span>
      <a-select-option
        v-for="leaf in group.leaves"
        :key="leaf.code"
        :value="leaf.code"
      >
        {{ leaf.code }} – {{ leaf.label }}
      </a-select-option>
    </a-select-opt-group>
  </a-select>
</template>

<script>
import { loadPlanComptable } from "@/utilities/depense/planComptable";
import { showApiError } from "@/utilities/showApiError";

/**
 * Searchable dropdown of the Tunisian Plan Comptable.
 *
 * The whole tree is loaded once and cached in memory. Only **leaf accounts**
 * (the most specific level reachable in the official chart) are selectable;
 * intermediate parents (single-digit root classes, level-2 totals, etc.) are
 * surfaced only as opt-group headers so users can scan them quickly.
 *
 * Emits the selected code via `v-model`; empty string when cleared.
 */
export default {
  name: "PlanComptableSelect",
  model: {
    prop: "value",
    event: "change",
  },
  props: {
    value: { type: String, default: "" },
    placeholder: { type: String, default: "" },
    disabled: { type: Boolean, default: false },
    allowClear: { type: Boolean, default: true },
  },
  data() {
    return {
      loading: false,
      groups: [],
    };
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const data = await loadPlanComptable();
        this.groups = this.buildGroups(data);
      } catch (e) {
        showApiError(this, e);
        this.groups = [];
      } finally {
        this.loading = false;
      }
    },
    /**
     * Returns the list of root classes with their leaf descendants. A node is
     * considered a leaf when no other node in the chart references it as a
     * parent.
     */
    buildGroups(data) {
      const flat = (data && data.flat) || [];
      const tree = (data && data.tree) || [];
      const parentCodes = new Set();
      for (const node of flat) {
        if (node.parentCode) parentCodes.add(String(node.parentCode));
      }
      const groups = [];
      for (const root of tree) {
        const rootCode = String(root.code);
        const leaves = flat
          .filter((n) => {
            if (!n || !n.code) return false;
            if (String(n.code) === rootCode) return false;
            if (n.level <= 1) return false;
            if (parentCodes.has(String(n.code))) return false;
            // Only keep descendants of this root class.
            return String(n.code).charAt(0) === rootCode;
          })
          .sort((a, b) => String(a.code).localeCompare(String(b.code)));
        if (leaves.length) {
          groups.push({ code: rootCode, label: root.label, leaves });
        }
      }
      return groups;
    },
    filterOption(input, option) {
      if (!input) return true;
      const needle = String(input).toLowerCase();
      const text =
        option.componentOptions &&
        option.componentOptions.children &&
        option.componentOptions.children
          .map((c) => (c && c.text) || "")
          .join("");
      if (text && String(text).toLowerCase().indexOf(needle) >= 0) return true;
      const code = option.key ? String(option.key).toLowerCase() : "";
      if (code && code.indexOf(needle) >= 0) return true;
      return false;
    },
    onChange(value) {
      this.$emit("change", value || "");
    },
  },
};
</script>

<style scoped>
.plan-comptable-group-label {
  font-weight: 600;
}
</style>
